import React, { useRef, useEffect, useState, useCallback } from 'react';
import T from 'prop-types';

const WAVEFORM_BASE_COLOR = '#8c8c8c';
const WAVEFORM_PROGRESS_COLOR = '#2e2e80';
const WAVEFORM_HEIGHT = 120;

const CanvaWaveform = ({ peaks, width, episodeDuration, currentTime, loading }) => {
  const canvaEl = useRef(null);
  const loadingEl = useRef(null);
  const progress = episodeDuration ? currentTime / episodeDuration : 0;
  const [animate, setAnimate] = useState(true);

  const drawLines = useCallback(
    ({ t = 0, animate }) => {
      if (t < WAVEFORM_HEIGHT && animate) {
        requestAnimationFrame(() => drawLines({ t: t + 4, animate }));
      }

      const ctx = canvaEl?.current?.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.globalCompositeOperation = 'destination-atop';

      ctx.strokeStyle = WAVEFORM_BASE_COLOR;

      const halfHeight = WAVEFORM_HEIGHT / 2;
      ctx.beginPath();
      width &&
        peaks.getSamples(width).forEach((peak, x) => {
          let peakNew = animate ? (peak * t) / 2 / halfHeight : peak;
          const movePos = Math.floor(halfHeight - peakNew * halfHeight + 0.5);
          const linePos = Math.floor(halfHeight + peakNew * halfHeight + 0.5);
          ctx.moveTo(x, movePos);
          ctx.lineTo(x, linePos);
        });
      ctx.stroke();
    },
    [peaks, width]
  );

  const drawWaveform = useCallback(() => {
    const ctx = canvaEl?.current?.getContext('2d');

    if (!ctx) {
      return;
    }
    ctx.clearRect(0, 0, width, WAVEFORM_HEIGHT);

    ctx.fillStyle = WAVEFORM_PROGRESS_COLOR;
    ctx.fillRect(0, 0, progress * width, WAVEFORM_HEIGHT);

    drawLines({ t: 0, animate });

    ctx.globalCompositeOperation = 'source-over';

    setAnimate(false);
  }, [width, progress, drawLines, animate]);

  const drawLoadingWaves = useCallback(
    (i = 0) => {
      const ctx = loadingEl?.current?.getContext('2d');
      if (!ctx) {
        return;
      }
      ctx.clearRect(0, 0, width, WAVEFORM_HEIGHT);

      ctx.fillStyle = 'white';

      ctx.fillRect(0, 0, width, WAVEFORM_HEIGHT);
      ctx.strokeStyle = WAVEFORM_BASE_COLOR;

      ctx.beginPath();
      ctx.moveTo(0, WAVEFORM_HEIGHT / 2);
      for (let x = 0; x < width; x++) {
        ctx.lineTo(x, WAVEFORM_HEIGHT / 2 + Math.sin(x * 0.01 + i) * (WAVEFORM_HEIGHT / 2 - 10));
      }
      ctx.stroke();

      if (!peaks) {
        requestAnimationFrame(() => drawLoadingWaves(i + 0.01));
      }
    },
    [width, peaks]
  );

  useEffect(() => {
    if (peaks && canvaEl.current) {
      drawWaveform();
    } else {
      drawLoadingWaves();
    }
  }, [width, peaks, progress, drawWaveform, drawLoadingWaves]);

  return loading ? (
    <canvas ref={loadingEl} width={width} height={WAVEFORM_HEIGHT} data-testid="canvas-admarker" />
  ) : (
    <canvas ref={canvaEl} width={width} height={WAVEFORM_HEIGHT} data-testid="canvas-admarker" />
  );
};

export default CanvaWaveform;

CanvaWaveform.propTypes = {
  episodeDuration: T.number.isRequired,
  peaks: T.object,
  width: T.number.isRequired,
  currentTime: T.number.isRequired,
  loading: T.bool,
};
